import asyncFormSubmitter from '../../../assets/scripts/interfaces/async-form-submitter'
import Component from '../../../assets/scripts/modules/component'
import fireCustomEvent from '../../../assets/scripts/utilities/fire-custom-event'
import { hydratorRegisterComponent } from '../../../assets/scripts/utilities/hydrator'

function resetForm (formElement, initialFormData) {
  for (const fieldElement of Array.from(formElement.querySelectorAll('[name]'))) {
    fieldElement.value = initialFormData.get(fieldElement.name)
    fieldElement.dispatchEvent(new Event('change'))
    fieldElement.dispatchEvent(new Event('input'))
  }
}

function fillQueryParamters () {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(urlSearchParams.entries())
  for (const key in params) {
    const input = document.querySelector(`input[id="${key}"]`)
    if (input) {
      input.value = params[key]
      // Use closest to reliably find the text-field container
      const textField = input.closest('.text-field')
      if (textField) {
        textField.classList.add('text-field--has-value')
        textField.classList.add('text-field--has-changed')
      }
    }
  }
}

// Apply text-field state based on current input values (useful on back/forward navigation)
function applyTextFieldState (root = document) {
  const fields = root.querySelectorAll('input, textarea, select')
  for (const input of Array.from(fields)) {
    const textField = input.closest('.text-field')
    if (!textField) continue

    const hasValue = (
      input.tagName === 'SELECT'
        ? input.value !== '' && input.value !== null
        : (input.value != null && String(input.value).trim() !== '')
    )

    if (hasValue) {
      textField.classList.add('text-field--has-value')
      // Keep validation errors visible: has-changed hides .text-field__error via CSS.
      if (!textField.classList.contains('text-field--error')) {
        textField.classList.add('text-field--has-changed')
      }
    } else {
      textField.classList.remove('text-field--has-value')
    }
  }
}

class FormComponent extends Component {
  init () {
    this.submitButton = this.element.querySelector('button[type="submit"], input[type="submit"]')
    this._submitLocked = false
    this._recaptchaSubmitReady = false
    this.element.addEventListener('submit', this.preventMultipleSubmits.bind(this), true)
    const isInlineVariant = this.element.classList.contains('form--forminline')
    const asyncSubmit = this.element.dataset.async_submit === 'true'
    const element = this.element

    if (this.submitButton) {
      this.spinner = this.submitButton.querySelector('.button__icon--before')
    }

    if (isInlineVariant) {
      const cancelButton = this.element.querySelector('.form__reset')
      const initialFormData = new FormData(this.element)
      cancelButton.addEventListener('click', () => resetForm(this.element, initialFormData))

      // This code works as long as this variant is used only in forms with a single field
      const formActions = this.element.querySelector('.form__actions')
      const singleElement = this.element.querySelector('input, textarea, select')
      singleElement.addEventListener('focus', (event) => {
        setTimeout(function () {
          formActions.style.display = 'block'
          element.classList.add('expanded')
        }, 250)
      }, true)

      singleElement.addEventListener('blur', (event) => {
        setTimeout(function () {
          formActions.style.display = 'none'
          element.classList.remove('expanded')
        }, 250)
      }, true)
    }

    this.element.addEventListener('custom-submit', async (event) => {
      if (asyncSubmit) {
        const { submit: submitAsync } = asyncFormSubmitter(this.element.action, window.CNV_APP.csrfToken)
        const response = await submitAsync(new FormData(this.element))
        if (response.errors) {
          for (const [fieldName, errors] of response.errors) {
            fireCustomEvent('form-error', { formElement: this.element, fieldName, errors })
          }
        }
      } else {
        this.element.submit()
      }
    })

    if (asyncSubmit) {
      const { submit: submitAsync } = asyncFormSubmitter(this.element.action, window.CNV_APP.csrfToken)

      this.element.addEventListener('submit', async (event) => {
        event.preventDefault()
        const response = await submitAsync(new FormData(this.element))
        if (response.errors) {
          for (const [fieldName, errors] of response.errors) {
            fireCustomEvent('form-error', { formElement: this.element, fieldName, errors })
          }
        }
      })
    }

    fillQueryParamters()
    // Ensure text-field state reflects any persisted values (e.g., browser back/forward)
    applyTextFieldState(this.element)

    // Re-apply on pageshow (bfcache restores values without re-running JS in some cases)
    const onPageShow = () => {
      fillQueryParamters()
      applyTextFieldState(this.element)
    }
    window.addEventListener('pageshow', onPageShow)
  }

  preventMultipleSubmits (event) {
    if (this._submitLocked) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    if (
      !this.element.__azureSubmitBound &&
      !this._recaptchaSubmitReady &&
      this.getRecaptchaSiteKey()
    ) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.refreshRecaptchaAndResubmit()
      return
    }

    this._recaptchaSubmitReady = false
    this._submitLocked = true

    // Disable after the current submit event so name/value of the clicked button are preserved.
    setTimeout(() => {
      if (this.element.__submitValidationAborted) {
        this.element.__submitValidationAborted = false
        this._submitLocked = false
        return
      }

      this.lockSubmitButtons()
    }, 0)
  }

  getRecaptchaSiteKey () {
    const script = this.element.querySelector('script[src*="recaptcha/api.js?render="]') ||
      document.querySelector('script[src*="recaptcha/api.js?render="]')
    if (!script) {
      return null
    }

    try {
      return new URL(script.src).searchParams.get('render')
    } catch (error) {
      return null
    }
  }

  ensureRecaptchaInput () {
    const inputs = this.element.querySelectorAll('[name=g-recaptcha-response]')
    let input = inputs[0]
    for (let i = 1; i < inputs.length; i++) {
      inputs[i].remove()
    }
    if (!input) {
      input = document.createElement('input')
      input.setAttribute('type', 'hidden')
      input.setAttribute('name', 'g-recaptcha-response')
      this.element.appendChild(input)
    }
    return input
  }

  waitForGrecaptcha (timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(resolve)
        return
      }

      const deadline = Date.now() + timeoutMs
      const tick = () => {
        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(resolve)
        } else if (Date.now() > deadline) {
          reject(new Error('reCAPTCHA not loaded'))
        } else {
          setTimeout(tick, 50)
        }
      }
      tick()
    })
  }

  async refreshRecaptchaToken () {
    const siteKey = this.getRecaptchaSiteKey()
    if (!siteKey) {
      return null
    }

    await this.waitForGrecaptcha()
    const input = this.ensureRecaptchaInput()
    const token = await window.grecaptcha.execute(siteKey, { action: 'submit' })
    input.value = token
    return token
  }

  async refreshRecaptchaAndResubmit () {
    try {
      await this.refreshRecaptchaToken()
      this._recaptchaSubmitReady = true
      if (typeof this.element.requestSubmit === 'function') {
        this.element.requestSubmit()
      } else {
        this._submitLocked = true
        this.lockSubmitButtons()
        this.element.submit()
      }
    } catch (error) {
      console.error('reCAPTCHA refresh failed', error)
      this._recaptchaSubmitReady = false
      this.enableSubmitButtons()
      this.showRecaptchaError()
    }
  }

  showRecaptchaError () {
    const wrapper = this.element.querySelector('.form__wrapper') || this.element
    const existing = wrapper.querySelector('.form__text--error.form__recaptcha-error')
    if (existing) {
      existing.remove()
    }

    const errorBlock = document.createElement('div')
    errorBlock.className = 'form__text form__text--error form__recaptcha-error'
    errorBlock.setAttribute('role', 'alert')
    const paragraph = document.createElement('p')
    paragraph.textContent = 'De beveiligingscontrole is mislukt. Vernieuw de pagina en probeer het opnieuw.'
    errorBlock.appendChild(paragraph)
    wrapper.insertBefore(errorBlock, wrapper.firstChild)
    errorBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  lockSubmitButtons () {
    for (const button of this.element.querySelectorAll('button[type="submit"], input[type="submit"]')) {
      button.disabled = true
    }

    this.showSubmitLoader()
    this.element.setAttribute('aria-busy', 'true')
  }

  enableSubmitButtons () {
    this._submitLocked = false

    for (const button of this.element.querySelectorAll('button[type="submit"], input[type="submit"]')) {
      button.disabled = false
    }

    this.hideSubmitLoader()
    this.element.removeAttribute('aria-busy')
  }

  getSubmitButtonContainer () {
    if (!this.submitButton) {
      return null
    }

    return this.submitButton.closest('.form__actions, .form-membership__form-btn-submit')
  }

  ensureSubmitLoader () {
    if (this.submitLoader || !this.submitButton) {
      return
    }

    const container = this.getSubmitButtonContainer()
    if (!container) {
      return
    }

    this.submitLoader = document.createElement('span')
    this.submitLoader.className = 'form__submit-loader'
    this.submitLoader.setAttribute('aria-hidden', 'true')
    this.submitLoader.hidden = true
    this.submitButton.insertAdjacentElement('afterend', this.submitLoader)
  }

  showSubmitLoader () {
    const hasBuiltInLoader = this.submitButton?.classList.contains('button--with-loader')

    if (hasBuiltInLoader && this.spinner) {
      this.spinner.style.display = 'block'
      return
    }

    this.ensureSubmitLoader()
    if (this.submitLoader) {
      this.submitLoader.hidden = false
    }
  }

  hideSubmitLoader () {
    if (this.spinner) {
      this.spinner.style.display = ''
    }

    if (this.submitLoader) {
      this.submitLoader.hidden = true
    }
  }
}

window.addEventListener('init-load', () => document.querySelectorAll('.form').forEach(element => {
  element.instance = element.instance || new FormComponent(element)
}))

hydratorRegisterComponent('.form', FormComponent)
